// src/pages/LoginScreen.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useApp } from '../lib/AppContext';
import { t } from '../lib/i18n';

export default function LoginScreen() {
  const { lang, showToast } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { showToast('Please enter email and password', 'error'); return; }
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { showToast(error.message, 'error'); setLoading(false); return; }
    // Don't navigate manually - App.js redirects once user state is set
    showToast('Welcome back!', 'success');
  };

  const handleOAuth = async (provider) => {
    setOauthLoading(provider);
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/account` },
    });
    if (error) { showToast(error.message, 'error'); setOauthLoading(''); }
  };

  return (
    <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 16px' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.6rem', letterSpacing: '.08em', color: '#4cca74', marginBottom: 6 }}>
            CosmiqHerbz
          </div>
          <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '2.2rem', letterSpacing: '.05em', marginBottom: 8 }}>
            {t('sign_in', lang)}
          </h1>
          <p style={{ color: '#666', fontSize: '.88rem', fontWeight: 300 }}>
            {t('no_account', lang)}{' '}
            <Link to="/signup" style={{ color: '#4cca74', fontWeight: 600, textDecoration: 'none' }}>
              {t('create_account', lang)}
            </Link>
          </p>
        </div>

        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div className="input-label">{t('email', lang)}</div>
            <input className="input-field" type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
          </div>
          <div>
            <div className="input-label">{t('password', lang)}</div>
            <input className="input-field" type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" />
          </div>
          <div style={{ textAlign: 'right' }}>
            <button type="button" style={{ background: 'none', border: 'none', color: '#555', fontSize: '.82rem', cursor: 'pointer' }}>
              {t('forgot_password', lang)}
            </button>
          </div>
          <button className="btn-primary" type="submit" disabled={loading} style={{ width: '100%' }}>
            {loading ? <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2, margin: '0 auto' }} /> : t('sign_in', lang)}
          </button>
        </form>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0' }}>
          <div style={{ flex: 1, height: 1, background: '#222' }} />
          <span style={{ color: '#444', fontSize: '.75rem', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '.1em', textTransform: 'uppercase' }}>
            {t('or_continue', lang)}
          </span>
          <div style={{ flex: 1, height: 1, background: '#222' }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={() => handleOAuth('google')}
            disabled={!!oauthLoading}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: '#fff', color: '#111', border: 'none', borderRadius: 25, padding: '12px 24px', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.88rem', fontWeight: 700, letterSpacing: '.08em', cursor: 'pointer', width: '100%' }}
          >
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.35-8.16 2.35-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            {oauthLoading === 'google' ? <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> : t('google', lang)}
          </button>
          <button
            onClick={() => handleOAuth('apple')}
            disabled={!!oauthLoading}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 25, padding: '12px 24px', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.88rem', fontWeight: 700, letterSpacing: '.08em', cursor: 'pointer', width: '100%' }}
          >
            <svg width="18" height="18" viewBox="0 0 256 256" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M213.1 78.9c-1.5 1.1-27.5 15.8-27.5 48.4 0 37.7 33.1 51.1 34 51.4-.1.8-5.3 18.3-17.5 36.1-10.8 15.7-22.2 31.3-39.5 31.3s-21.7-10.1-41.7-10.1c-19.5 0-26.4 10.4-42.2 10.4S55.4 235 43 217.5C28.8 197.9 17 166.9 17 137.5c0-49.3 32.2-75.4 63.8-75.4 16.8 0 30.8 11.1 41.4 11.1 10.1 0 25.9-11.8 44.9-11.8 7.2 0 33.2.7 50.2 25.2zm-58.7-47.6C162.6 22.3 169 11 169 0c0-.8-.1-1.7-.2-2.5-9.1.3-19.9 6.1-26.4 13.8-5.9 7-13.3 18.5-13.3 31.5 0 .9.2 1.9.2 2.2.6.1 1.5.2 2.4.2 8.1 0 18.2-5.4 22.7-13.9z"/></svg>
            {oauthLoading === 'apple' ? <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2, borderColor: '#fff', borderTopColor: '#555' }} /> : t('apple', lang)}
          </button>
        </div>
      </div>
    </div>
  );
}